import React from 'react';

import PlusFlair from './PlusFlair';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faUserCircle } from '@fortawesome/free-solid-svg-icons';

import bumpIcon from '../images/ic_bump.png';
import superBumpIcon from '../images/ic_super_bump.png';
import { superBumpGold, bumpSilver } from '../config/palette';

export interface JukeboxRowAction {
  key: string;
  label: string;
  icon?: any;
  /** Renders the gold or silver bump treatment instead of the default outline. */
  tier?: 'bump' | 'superBump';
  /** Draws the button as already applied - a favourited song, a video already bumped. */
  active?: boolean;
  disabled?: boolean;
  onClick(): void;
}

interface JukeboxVideoRowProps {
  /** A search result, a favourite or a queue video. They differ, so only the shared fields are read. */
  video: any;
  isBump?: boolean;
  isSuperBump?: boolean;
  /** Shown above the queue's first row. */
  eyebrow?: string;
  /** A line under the song, such as why this venue will not take it. */
  note?: string;
  actions: JukeboxRowAction[];
}

/**
 * One song, on a phone.
 *
 * The player screen's own row (CollapsableVideoListItem) is a horizontal strip sized for a
 * television and its buttons take no argument, so it cannot act on the video it is drawn
 * for. This is the customer-facing equivalent: a vertical row whose buttons know which
 * song they belong to.
 *
 * The bump colours and corner badges are the same ones the player screen uses, so a song a
 * patron has paid to move up looks the same on their phone as it does on the wall.
 */
const JukeboxVideoRow = (props: JukeboxVideoRowProps) => {
  const { video, isBump, isSuperBump, eyebrow, note, actions } = props;

  const thumbnailUrl = video.thumbnail && video.thumbnail.url;
  const ownerName = video.owner && video.owner.username;
  const ownerImage = video.owner && video.owner.profilePicUrl;

  let tierStyle: React.CSSProperties = {};
  if (isSuperBump) {
    tierStyle = { borderColor: superBumpGold, borderWidth: '3px', borderStyle: 'solid', boxShadow: `0 0 14px ${superBumpGold}` };
  } else if (isBump) {
    tierStyle = { borderColor: bumpSilver, borderWidth: '3px', borderStyle: 'solid', boxShadow: `0 0 12px ${bumpSilver}` };
  }

  return (
    <li className="jukebox-row">
      {!!eyebrow && <div className="jukebox-row-eyebrow">{eyebrow}</div>}
      <div className="jukebox-row-main">
        {/* A queue video keeps whatever thumbnail it was added with, and rows written
            straight through the API can arrive without one. An empty 120px column reads
            as an image that failed to load, so the column goes instead. */}
        {!!thumbnailUrl && (
          <div className="jukebox-row-thumb">
            <img src={thumbnailUrl} style={tierStyle} alt="" />
            {(isBump || isSuperBump) && (
              <img
                className="jukebox-row-tier-badge"
                src={isSuperBump ? superBumpIcon : bumpIcon}
                alt={isSuperBump ? 'Super bumped' : 'Bumped'}
              />
            )}
          </div>
        )}

        <div className="jukebox-row-body">
          <div className="jukebox-row-title">{video.title}</div>
          <div className="jukebox-row-meta">
            {!!ownerName && (
              <span className="jukebox-row-owner">
                <PlusFlair isPlus={video.owner.isPlus}>
                  {ownerImage ? (
                    <img className="jukebox-row-avatar" src={ownerImage} alt="" />
                  ) : (
                    <FontAwesomeIcon icon={faUserCircle} className="jukebox-row-avatar-icon" />
                  )}
                </PlusFlair>{' '}
                {ownerName.toUpperCase()}
              </span>
            )}
            {!!video.duration && (
              <span className="jukebox-row-duration">
                <FontAwesomeIcon icon={faClock} /> {video.duration}
              </span>
            )}
          </div>
          {!!note && <div className="jukebox-row-note">{note}</div>}
        </div>
      </div>

      {actions.length > 0 && (
        <div className="jukebox-row-actions">
          {actions.map(action => {
            const classNames = ['jukebox-action'];
            if (action.tier) {
              classNames.push(`jukebox-action-${action.tier}`);
            }
            if (action.active) {
              classNames.push('jukebox-action-active');
            }
            return (
              <button
                key={action.key}
                type="button"
                className={classNames.join(' ')}
                disabled={action.disabled}
                onClick={action.onClick}
              >
                {!!action.icon && <FontAwesomeIcon icon={action.icon} />} {action.label}
              </button>
            );
          })}
        </div>
      )}
    </li>
  );
};

export default JukeboxVideoRow;
